/**
 * Extract tweet ID from various Twitter URL formats
 * Supports:
 * - https://twitter.com/username/status/123456
 * - https://x.com/username/status/123456
 * - twitter.com/username/status/123456 (no protocol)
 * - Mobile URLs
 */
export const extractTweetId = (url) => {
  if (!url || typeof url !== "string") {
    return null;
  }

  // Remove whitespace
  url = url.trim();

  // Add protocol if missing
  if (!url.startsWith("http://") && !url.startsWith("https://")) {
    url = "https://" + url;
  }

  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    // Check if it's a Twitter/X URL
    if (!hostname.includes("twitter.com") && !hostname.includes("x.com")) {
      return null;
    }

    // Extract status ID from path
    const pathParts = urlObj.pathname.split("/");
    const statusIndex = pathParts.indexOf("status");

    if (statusIndex !== -1 && statusIndex < pathParts.length - 1) {
      const tweetId = pathParts[statusIndex + 1];
      // Remove query parameters if any
      return tweetId.split("?")[0];
    }

    return null;
  } catch (error) {
    console.error("Error parsing URL:", error);
    return null;
  }
};

/**
 * Fetch tweet data using react-tweet API (preferred) or Twitter oEmbed API (fallback)
 * 
 * IMPORTANT: Twitter/X no longer includes engagement metrics in public HTML/oEmbed.
 * Metrics are only available through:
 * 1. react-tweet's getTweet() API (requires react-tweet package)
 * 2. Twitter API v2 (requires authentication)
 * 3. Authenticated scraping (requires session cookies)
 */
export const fetchTweetData = async (tweetId) => {
  // Try react-tweet first (it can get metrics)
  try {
    console.log("[Fetch Tweet] Attempting to use react-tweet API...");
    const tweetData = await fetchTweetWithReactTweet(tweetId);
    // Use react-tweet data if we have valid tweet data structure, even if metrics are 0
    if (tweetData && tweetData.id && tweetData.author) {
      console.log("[Fetch Tweet] Successfully fetched with react-tweet API");
      return tweetData;
    }
    console.log("[Fetch Tweet] react-tweet returned invalid data, falling back to oEmbed");
  } catch (error) {
    console.log("[Fetch Tweet] react-tweet not available, using oEmbed fallback:", error.message);
  }

  // Fallback to oEmbed (no metrics available)
  try {
    console.log("[Fetch Tweet] Using Twitter oEmbed API (metrics will not be available)");
    const oEmbedUrl = `https://publish.twitter.com/oembed?url=https://twitter.com/i/status/${tweetId}&omit_script=true`;

    // Try direct fetch first
    try {
      const response = await fetch(oEmbedUrl);
      if (!response.ok) {
        throw new Error("Failed to fetch tweet");
      }
      const data = await response.json();

      // Parse oEmbed HTML to extract tweet data (now async)
      return await parseOEmbedData(data, tweetId);
    } catch (corsError) {
      // If CORS fails, use a CORS proxy
      const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(
        oEmbedUrl
      )}`;
      const proxyResponse = await fetch(proxyUrl);
      const proxyData = await proxyResponse.json();
      const oEmbedData = JSON.parse(proxyData.contents);
      return await parseOEmbedData(oEmbedData, tweetId);
    }
  } catch (error) {
    console.error("Error fetching tweet:", error);
    throw new Error(
      error.message || "Failed to fetch tweet. Please check the URL."
    );
  }
};

/**
 * Extract engagement metrics from HTML
 */
const extractMetricsFromHTML = (html, source = "unknown") => {
  const metrics = {
    likes: 0,
    retweets: 0,
    replies: 0,
  };

  console.log(`[Metrics Extraction] Starting extraction from ${source}, HTML length: ${html ? html.length : 0}`);

  if (!html) {
    console.warn("[Metrics Extraction] No HTML provided");
    return metrics;
  }

  // Try to find metrics in JSON-LD structured data
  const jsonLdMatch = html.match(/<script[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/i);
  if (jsonLdMatch) {
    console.log("[Metrics Extraction] Found JSON-LD data");
    try {
      const jsonData = JSON.parse(jsonLdMatch[1]);
      console.log("[Metrics Extraction] Parsed JSON-LD:", JSON.stringify(jsonData).substring(0, 500));
      if (jsonData.interactionStatistic) {
        jsonData.interactionStatistic.forEach((stat) => {
          const count = parseInt(stat.userInteractionCount || 0);
          if (stat.interactionType === "https://schema.org/LikeAction") {
            metrics.likes = count;
            console.log(`[Metrics Extraction] Found likes from JSON-LD: ${count}`);
          } else if (stat.interactionType === "https://schema.org/ShareAction") {
            metrics.retweets = count;
            console.log(`[Metrics Extraction] Found retweets from JSON-LD: ${count}`);
          } else if (stat.interactionType === "https://schema.org/CommentAction") {
            metrics.replies = count;
            console.log(`[Metrics Extraction] Found replies from JSON-LD: ${count}`);
          }
        });
      }
    } catch (e) {
      console.warn("[Metrics Extraction] Failed to parse JSON-LD:", e);
    }
  } else {
    console.log("[Metrics Extraction] No JSON-LD found");
  }

  // Extract from data attributes and text patterns
  // Likes
  const likesPatterns = [
    /data-testid=["']like["'][^>]*>[\s\S]{0,500}?(\d+\.?\d*[KMB]?)/i,
    /"like_count":\s*(\d+)/i,
    /"favorite_count":\s*(\d+)/i,
    /(\d+\.?\d*[KMB]?)\s*(?:likes?|Like)/i,
    /<span[^>]*>(\d+\.?\d*[KMB]?)<\/span>\s*likes?/i,
    /(\d+\.?\d*[KMB]?)\s*likes?/i,
    /aria-label=["'][^"']*?(\d+\.?\d*[KMB]?)\s*(?:likes?|Like|favorites?)/i,
  ];

  console.log("[Metrics Extraction] Searching for likes...");
  for (let i = 0; i < likesPatterns.length; i++) {
    const pattern = likesPatterns[i];
    // Ensure pattern has global flag for matchAll
    const globalPattern = pattern.global ? pattern : new RegExp(pattern.source, pattern.flags + 'g');
    try {
      const matches = [...html.matchAll(globalPattern)];
      for (const match of matches) {
        if (match && match[1]) {
          const count = parseCount(match[1]);
          console.log(`[Metrics Extraction] Likes pattern ${i + 1} matched: "${match[1]}" -> ${count}`);
          if (count > 0 && count > metrics.likes) {
            metrics.likes = count;
          }
        }
      }
    } catch (e) {
      // Fallback to regular match if matchAll fails
      const match = html.match(pattern);
      if (match && match[1]) {
        const count = parseCount(match[1]);
        console.log(`[Metrics Extraction] Likes pattern ${i + 1} matched (fallback): "${match[1]}" -> ${count}`);
        if (count > 0 && count > metrics.likes) {
          metrics.likes = count;
        }
      }
    }
    if (metrics.likes > 0) break;
  }

  // Retweets
  const retweetPatterns = [
    /data-testid=["']retweet["'][^>]*>[\s\S]{0,500}?(\d+\.?\d*[KMB]?)/i,
    /"retweet_count":\s*(\d+)/i,
    /(\d+\.?\d*[KMB]?)\s*(?:retweets?|Retweet)/i,
    /<span[^>]*>(\d+\.?\d*[KMB]?)<\/span>\s*retweets?/i,
    /(\d+\.?\d*[KMB]?)\s*retweets?/i,
    /aria-label=["'][^"']*?(\d+\.?\d*[KMB]?)\s*(?:retweets?|Retweet)/i,
  ];

  console.log("[Metrics Extraction] Searching for retweets...");
  for (let i = 0; i < retweetPatterns.length; i++) {
    const pattern = retweetPatterns[i];
    // Ensure pattern has global flag for matchAll
    const globalPattern = pattern.global ? pattern : new RegExp(pattern.source, pattern.flags + 'g');
    try {
      const matches = [...html.matchAll(globalPattern)];
      for (const match of matches) {
        if (match && match[1]) {
          const count = parseCount(match[1]);
          console.log(`[Metrics Extraction] Retweets pattern ${i + 1} matched: "${match[1]}" -> ${count}`);
          if (count > 0 && count > metrics.retweets) {
            metrics.retweets = count;
          }
        }
      }
    } catch (e) {
      // Fallback to regular match if matchAll fails
      const match = html.match(pattern);
      if (match && match[1]) {
        const count = parseCount(match[1]);
        console.log(`[Metrics Extraction] Retweets pattern ${i + 1} matched (fallback): "${match[1]}" -> ${count}`);
        if (count > 0 && count > metrics.retweets) {
          metrics.retweets = count;
        }
      }
    }
    if (metrics.retweets > 0) break;
  }

  // Replies
  const replyPatterns = [
    /data-testid=["']reply["'][^>]*>[\s\S]{0,500}?(\d+\.?\d*[KMB]?)/i,
    /"reply_count":\s*(\d+)/i,
    /(\d+\.?\d*[KMB]?)\s*(?:replies?|Reply)/i,
    /<span[^>]*>(\d+\.?\d*[KMB]?)<\/span>\s*replies?/i,
    /(\d+\.?\d*[KMB]?)\s*replies?/i,
    /aria-label=["'][^"']*?(\d+\.?\d*[KMB]?)\s*(?:replies?|Reply)/i,
  ];

  console.log("[Metrics Extraction] Searching for replies...");
  for (let i = 0; i < replyPatterns.length; i++) {
    const pattern = replyPatterns[i];
    // Ensure pattern has global flag for matchAll
    const globalPattern = pattern.global ? pattern : new RegExp(pattern.source, pattern.flags + 'g');
    try {
      const matches = [...html.matchAll(globalPattern)];
      for (const match of matches) {
        if (match && match[1]) {
          const count = parseCount(match[1]);
          console.log(`[Metrics Extraction] Replies pattern ${i + 1} matched: "${match[1]}" -> ${count}`);
          if (count > 0 && count > metrics.replies) {
            metrics.replies = count;
          }
        }
      }
    } catch (e) {
      // Fallback to regular match if matchAll fails
      const match = html.match(pattern);
      if (match && match[1]) {
        const count = parseCount(match[1]);
        console.log(`[Metrics Extraction] Replies pattern ${i + 1} matched (fallback): "${match[1]}" -> ${count}`);
        if (count > 0 && count > metrics.replies) {
          metrics.replies = count;
        }
      }
    }
    if (metrics.replies > 0) break;
  }

  // Try to extract from JavaScript data objects (Twitter embeds data in script tags)
  console.log("[Metrics Extraction] Searching for JavaScript data objects...");

  // Look for window.__INITIAL_STATE__ or similar data structures
  const scriptMatches = html.match(/<script[^>]*>([\s\S]*?)<\/script>/gi);
  if (scriptMatches) {
    console.log(`[Metrics Extraction] Found ${scriptMatches.length} script tags`);
    for (let i = 0; i < scriptMatches.length; i++) {
      const script = scriptMatches[i];
      // Get full script content - metrics might be in large data structures
      const scriptContent = script;

      // First, try to find the actual tweet data structure
      // Twitter often embeds data like: "favorite_count":770 or "retweet_count":137
      // Look for these patterns more broadly
      const broadPatterns = [
        { pattern: /"favorite_count"\s*:\s*(\d+)/gi, type: 'likes', name: 'favorite_count' },
        { pattern: /"like_count"\s*:\s*(\d+)/gi, type: 'likes', name: 'like_count' },
        { pattern: /"retweet_count"\s*:\s*(\d+)/gi, type: 'retweets', name: 'retweet_count' },
        { pattern: /"reply_count"\s*:\s*(\d+)/gi, type: 'replies', name: 'reply_count' },
        { pattern: /"quote_count"\s*:\s*(\d+)/gi, type: 'replies', name: 'quote_count' },
        { pattern: /favorite_count["\s]*:[\s]*(\d+)/gi, type: 'likes', name: 'favorite_count_alt' },
        { pattern: /retweet_count["\s]*:[\s]*(\d+)/gi, type: 'retweets', name: 'retweet_count_alt' },
        { pattern: /reply_count["\s]*:[\s]*(\d+)/gi, type: 'replies', name: 'reply_count_alt' },
      ];

      console.log(`[Metrics Extraction] Searching script ${i} (${scriptContent.length} chars)...`);
      for (const { pattern, type, name } of broadPatterns) {
        try {
          const matches = [...scriptContent.matchAll(pattern)];
          for (const match of matches) {
            if (match && match[1]) {
              const count = parseInt(match[1]);
              if (count > 0) {
                console.log(`[Metrics Extraction] Found ${name} in script ${i}: ${count}`);
                if (type === 'likes' && count > metrics.likes) {
                  metrics.likes = count;
                } else if (type === 'retweets' && count > metrics.retweets) {
                  metrics.retweets = count;
                } else if (type === 'replies' && count > metrics.replies) {
                  metrics.replies = count;
                }
              }
            }
          }
        } catch (e) {
          // Continue to next pattern
        }
      }

      // Also search the full script content with the original patterns (in case broadPatterns missed something)
      // This is a fallback search

      // Try to parse as JSON if it looks like JSON
      try {
        const jsonMatch = scriptContent.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const jsonData = JSON.parse(jsonMatch[0]);
          // Recursively search for metrics in JSON
          const searchJson = (obj, path = '') => {
            if (typeof obj !== 'object' || obj === null) return;
            for (const key in obj) {
              const value = obj[key];
              const currentPath = path ? `${path}.${key}` : key;
              if (key === 'favorite_count' || key === 'like_count') {
                const count = parseInt(value);
                if (count > metrics.likes) {
                  metrics.likes = count;
                  console.log(`[Metrics Extraction] Found likes in JSON at ${currentPath}: ${count}`);
                }
              } else if (key === 'retweet_count') {
                const count = parseInt(value);
                if (count > metrics.retweets) {
                  metrics.retweets = count;
                  console.log(`[Metrics Extraction] Found retweets in JSON at ${currentPath}: ${count}`);
                }
              } else if (key === 'reply_count' || key === 'quote_count') {
                const count = parseInt(value);
                if (count > metrics.replies) {
                  metrics.replies = count;
                  console.log(`[Metrics Extraction] Found replies in JSON at ${currentPath}: ${count}`);
                }
              } else if (typeof value === 'object') {
                searchJson(value, currentPath);
              }
            }
          };
          searchJson(jsonData);
        }
      } catch (e) {
        // Not JSON, continue
      }
    }
  }

  // Log sample HTML for debugging
  if (metrics.likes === 0 && metrics.retweets === 0 && metrics.replies === 0) {
    console.warn("[Metrics Extraction] No metrics found. Sample HTML (first 2000 chars):", html.substring(0, 2000));
    // Try to find any numbers that might be metrics
    const allNumbers = html.match(/(\d+\.?\d*[KMB]?)/g);
    if (allNumbers) {
      console.log("[Metrics Extraction] Found numbers in HTML:", allNumbers.slice(0, 20));
      // Look for numbers near metric-related text
      const contextPattern = /(?:like|retweet|reply|favorite|comment)[^>]*>[\s\S]{0,100}?(\d+\.?\d*[KMB]?)/gi;
      const contextMatches = [...html.matchAll(contextPattern)];
      if (contextMatches.length > 0) {
        console.log("[Metrics Extraction] Found numbers near metric keywords:", contextMatches.slice(0, 10).map(m => ({ text: m[0].substring(0, 50), number: m[1] })));
      }
    }

    // Search for more patterns in the full HTML
    console.log("[Metrics Extraction] Searching deeper in HTML...");
    const deepPatterns = [
      /(\d+\.?\d*[KMB]?)\s*(?:likes?|Like|favorites?)/gi,
      /(\d+\.?\d*[KMB]?)\s*(?:retweets?|Retweet)/gi,
      /(\d+\.?\d*[KMB]?)\s*(?:replies?|Reply|comments?)/gi,
    ];

    for (let i = 0; i < deepPatterns.length; i++) {
      const pattern = deepPatterns[i];
      const matches = [...html.matchAll(pattern)];
      if (matches.length > 0) {
        console.log(`[Metrics Extraction] Found ${matches.length} potential matches for pattern ${i + 1}:`, matches.slice(0, 5).map(m => m[1]));
        // Try the first few matches
        for (const match of matches.slice(0, 3)) {
          const count = parseCount(match[1]);
          if (count > 0) {
            if (i === 0 && count > metrics.likes) {
              metrics.likes = count;
              console.log(`[Metrics Extraction] Set likes to ${count} from deep search`);
            } else if (i === 1 && count > metrics.retweets) {
              metrics.retweets = count;
              console.log(`[Metrics Extraction] Set retweets to ${count} from deep search`);
            } else if (i === 2 && count > metrics.replies) {
              metrics.replies = count;
              console.log(`[Metrics Extraction] Set replies to ${count} from deep search`);
            }
          }
        }
      }
    }
  }

  console.log("[Metrics Extraction] Final metrics:", metrics);
  return metrics;
};

/**
 * Fetch engagement metrics (likes, retweets, replies) from tweet page
 * 
 * DEPRECATED: Twitter/X no longer includes engagement metrics in public HTML.
 * This function is kept for backwards compatibility but will always return zeros.
 * Use react-tweet's getTweet() API or Twitter API v2 for metrics.
 */
const fetchEngagementMetrics = async (tweetId) => {
  console.warn(`[Fetch Metrics] Twitter/X no longer provides engagement metrics in public HTML. Metrics will be 0.`);
  return {
    likes: 0,
    retweets: 0,
    replies: 0,
  };
};

/**
 * Parse count string like "2.3K", "1.2M", "1,234" to number
 */
const parseCount = (str) => {
  if (!str) {
    console.log("[Parse Count] Empty string, returning 0");
    return 0;
  }

  const originalStr = str;
  // Remove commas
  str = str.replace(/,/g, "");

  // Handle K, M, B suffixes
  const num = parseFloat(str);
  if (isNaN(num)) {
    console.log(`[Parse Count] Could not parse "${originalStr}" as number, returning 0`);
    return 0;
  }

  let result = 0;
  if (str.toUpperCase().includes("K")) {
    result = Math.round(num * 1000);
    console.log(`[Parse Count] Parsed "${originalStr}" as ${result} (K multiplier)`);
  } else if (str.toUpperCase().includes("M")) {
    result = Math.round(num * 1000000);
    console.log(`[Parse Count] Parsed "${originalStr}" as ${result} (M multiplier)`);
  } else if (str.toUpperCase().includes("B")) {
    result = Math.round(num * 1000000000);
    console.log(`[Parse Count] Parsed "${originalStr}" as ${result} (B multiplier)`);
  } else {
    result = Math.round(num);
    console.log(`[Parse Count] Parsed "${originalStr}" as ${result} (no multiplier)`);
  }

  return result;
};

/**
 * Fetch full name (display name) from username
 */
const fetchFullNameFromUsername = async (username) => {
  if (!username || username === "unknown") return "";

  try {
    // Try fetching Twitter profile page via proxy
    const profileUrl = `https://twitter.com/${username}`;
    const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(profileUrl)}`;
    const response = await fetch(proxyUrl);
    const data = await response.json();
    const html = data.contents;

    // Extract full name from profile page
    // Look for meta tags or structured data
    const metaNameMatch = html.match(/<meta[^>]*property=["']og:title["'][^>]*content=["']([^"']+)["']/i);
    if (metaNameMatch && metaNameMatch[1]) {
      const fullName = metaNameMatch[1].trim();
      // Remove " on Twitter" suffix if present
      return fullName.replace(/\s+on\s+Twitter$/i, "").trim();
    }

    // Try to find in title tag
    const titleMatch = html.match(/<title[^>]*>([^<]+)<\/title>/i);
    if (titleMatch && titleMatch[1]) {
      const title = titleMatch[1].trim();
      // Format: "Name (@username) / Twitter" or "Name (@username) on X"
      const nameMatch = title.match(/^([^(]+)\s*\(@/);
      if (nameMatch && nameMatch[1]) {
        return nameMatch[1].trim();
      }
    }

    // Try to find in structured data (JSON-LD)
    const jsonLdMatch = html.match(/<script[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/i);
    if (jsonLdMatch) {
      try {
        const jsonData = JSON.parse(jsonLdMatch[1]);
        if (jsonData.name) {
          return jsonData.name;
        }
        if (jsonData.author && jsonData.author.name) {
          return jsonData.author.name;
        }
      } catch (e) {
        // Ignore JSON parse errors
      }
    }
  } catch (error) {
    // Silently fail
  }

  return "";
};

/**
 * Fetch profile image URL from username (fallback method)
 * This uses a public API to get Twitter profile information
 */
const fetchProfileImageFromUsername = async (username) => {
  if (!username || username === "unknown") return "";

  try {
    // Method 1: Try using unavatar.io via CORS proxy (returns JSON) with multiple fallbacks
    try {
      const unavatarUrl = `https://unavatar.io/twitter/${username}?json=true`;

      // Try multiple CORS proxy options
      const proxyOptions = [
        `https://api.allorigins.win/get?url=${encodeURIComponent(unavatarUrl)}`,
        `https://corsproxy.io/?${encodeURIComponent(unavatarUrl)}`,
        `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(unavatarUrl)}`,
      ];

      let data = null;
      let lastError = null;

      for (const proxyUrl of proxyOptions) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

          let response;
          try {
            response = await fetch(proxyUrl, {
              method: 'GET',
              headers: {
                'Accept': 'application/json, text/html, */*',
              },
              signal: controller.signal,
              mode: 'cors',
            });
            clearTimeout(timeoutId); // Clear timeout on success
          } catch (fetchError) {
            clearTimeout(timeoutId); // Always clear timeout
            // Check if it's an abort error (timeout) or other error
            if (fetchError.name === 'AbortError' || fetchError.message?.includes('aborted')) {
              lastError = new Error('Request timeout');
            } else {
              // CORS or network error - continue to next proxy
              lastError = new Error(`Network/CORS error: ${fetchError.message}`);
            }
            continue;
          }

          if (!response || !response.ok) {
            throw new Error(`Proxy returned ${response?.status || 'unknown'}: ${response?.statusText || 'Failed'}`);
          }

          try {
            data = await response.json();
            if (data && (data.contents || data.url)) {
              break;
            }
          } catch (jsonError) {
            // Try text parsing
            const textData = await response.text();
            if (textData) {
              try {
                data = JSON.parse(textData);
                if (data && (data.contents || data.url)) {
                  break;
                }
              } catch (e) {
                // Not JSON, continue to next proxy
              }
            }
          }
        } catch (proxyError) {
          lastError = proxyError;
          continue; // Try next proxy
        }
      }

      if (!data) {
        // All proxies failed - silently continue to next method
        throw new Error(`All proxy attempts failed`);
      }

      // unavatar.io returns JSON with avatar URL when json=true
      if (data.contents) {
        try {
          const unavatarData = JSON.parse(data.contents);
          if (unavatarData && unavatarData.url) {
            return unavatarData.url;
          }
        } catch (e) {
          // If parsing fails, try to extract URL from response text
          const urlMatch = data.contents.match(/https?:\/\/[^"'\s<>]*\.(jpg|png|jpeg|webp)/i);
          if (urlMatch) {
            return urlMatch[0];
          }
        }
      }
    } catch (e) {
      // Silently continue to next method
    }

    // Method 2: Try fetching Twitter profile page via proxy (with multiple fallbacks)
    try {
      const profileUrl = `https://twitter.com/${username}`;

      // Try multiple CORS proxy options
      const proxyOptions = [
        `https://api.allorigins.win/get?url=${encodeURIComponent(profileUrl)}`,
        `https://corsproxy.io/?${encodeURIComponent(profileUrl)}`,
        `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(profileUrl)}`,
      ];

      let html = null;
      let lastError = null;

      for (const proxyUrl of proxyOptions) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

          let response;
          try {
            response = await fetch(proxyUrl, {
              method: 'GET',
              headers: {
                'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
              },
              signal: controller.signal,
              mode: 'cors',
            });
            clearTimeout(timeoutId); // Clear timeout on success
          } catch (fetchError) {
            clearTimeout(timeoutId); // Always clear timeout
            // Check if it's an abort error (timeout) or other error
            if (fetchError.name === 'AbortError' || fetchError.message?.includes('aborted')) {
              lastError = new Error('Request timeout');
            } else {
              // CORS or network error - continue to next proxy
              lastError = new Error(`Network/CORS error: ${fetchError.message}`);
            }
            continue;
          }

          if (!response || !response.ok) {
            throw new Error(`Proxy returned ${response?.status || 'unknown'}: ${response?.statusText || 'Failed'}`);
          }

          let data;
          try {
            data = await response.json();
          } catch (jsonError) {
            const textData = await response.text();
            if (textData && textData.length > 100) {
              html = textData;
              break;
            }
            throw new Error('Failed to parse response');
          }

          // AllOrigins returns {contents: "..."}, other proxies might return HTML directly
          if (data && data.contents) {
            html = data.contents;
          } else if (typeof data === 'string') {
            html = data;
          } else {
            throw new Error('Unexpected proxy response format');
          }

          if (html && html.length > 100) {
            break;
          }
        } catch (proxyError) {
          lastError = proxyError;
          continue; // Try next proxy
        }
      }

      if (!html) {
        // All proxies failed - silently continue to next method
        throw new Error(`All proxy attempts failed`);
      }

      // Extract profile image from the HTML with multiple patterns
      const patterns = [
        /(https?:\/\/[^"'\s<>]*profile_images[^"'\s<>]*_normal\.(jpg|png|jpeg|webp))/i,
        /(https?:\/\/pbs\.twimg\.com\/profile_images\/[^"'\s<>]*_normal\.(jpg|png|jpeg|webp))/i,
        /profile_images\/[^"'\s<>]*_normal\.(jpg|png|jpeg|webp)/i
      ];

      for (const pattern of patterns) {
        const match = html.match(pattern);
        if (match) {
          let imageUrl = match[1] || match[0];
          // If URL doesn't start with http, prepend https://
          if (!imageUrl.startsWith("http")) {
            // Try to construct full URL
            if (imageUrl.includes("pbs.twimg.com")) {
              imageUrl = "https://" + imageUrl;
            } else if (imageUrl.startsWith("profile_images")) {
              imageUrl = "https://pbs.twimg.com/" + imageUrl;
            } else {
              imageUrl = "https://" + imageUrl;
            }
          }
          // Upgrade to better quality
          imageUrl = imageUrl.replace(/_normal\.(jpg|png|jpeg|webp)/i, "_400x400.$1");
          if (imageUrl && imageUrl.startsWith("http")) {
            return imageUrl;
          }
        }
      }
    } catch (error) {
      // Silently continue - profile image fetching is optional
      if (process.env.NODE_ENV === 'development') {
        console.warn("Error fetching profile from Twitter page:", error.message);
      }
    }

    // Method 3: Try alternative avatar services
    try {
      // Use avatars.dicebear.com as last resort (generates avatar from username)
      // This is not the real profile picture but better than nothing
      return `https://api.dicebear.com/7.x/avataaars/svg?seed=${username}`;
    } catch (e) {
      // Ignore
    }
  } catch (error) {
    // Silently fail - profile image is optional
    if (process.env.NODE_ENV === 'development') {
      console.warn("Error fetching profile image:", error?.message || error);
    }
  }

  // Return empty string if all methods fail - profile image is optional
  return "";
};

/**
 * Parse oEmbed data into a structured format
 */
const parseOEmbedData = async (oEmbedData, tweetId) => {
  // Create a temporary DOM element to parse HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = oEmbedData.html;

  // Extract basic information
  const username = extractUsername(oEmbedData.url);
  let authorName = "";
  let avatar = extractProfilePicture(oEmbedData.html, username);

  // Method 1: Try to get author_name from oEmbed response (most reliable)
  if (oEmbedData.author_name) {
    authorName = oEmbedData.author_name.trim();
  }

  // Method 2: If not in oEmbed response, try to extract from HTML
  if (!authorName || authorName === "Unknown") {
    authorName = extractAuthorName(oEmbedData.html);
  }

  // Method 3: If still not found or looks like a URL/date, fetch from profile page
  if ((!authorName ||
    authorName === "Unknown" ||
    authorName.startsWith("http") ||
    /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{1,2},\s+\d{4}$/i.test(authorName.trim())) &&
    username && username !== "unknown") {
    try {
      const fetchedName = await fetchFullNameFromUsername(username);
      if (fetchedName && fetchedName.trim()) {
        authorName = fetchedName.trim();
      }
    } catch (error) {
      // Silently fail - name is optional
    }
  }

  // Final fallback: use username as display name if nothing else works
  if (!authorName || authorName === "Unknown" || authorName.startsWith("http")) {
    authorName = username.charAt(0).toUpperCase() + username.slice(1);
  }

  // If avatar not found in oEmbed, try to fetch it separately
  if (!avatar && username && username !== "unknown") {
    try {
      avatar = await fetchProfileImageFromUsername(username);
    } catch (error) {
      // Silently fail - profile image is optional
      // Don't let profile image fetch errors break the entire tweet fetch
      if (process.env.NODE_ENV === 'development') {
        console.warn("Failed to fetch profile image separately:", error?.message || error);
      }
      // Continue without profile image - it's not critical
      avatar = ""; // Set to empty string to prevent undefined
    }
  }

  // IMPORTANT: Twitter/X no longer includes engagement metrics in oEmbed HTML
  // Metrics are only available through:
  // 1. react-tweet's getTweet() API (requires react-tweet package)
  // 2. Twitter API v2 (requires authentication)
  // 3. Authenticated scraping (requires session cookies)
  // 
  // Setting metrics to 0 - they will only be populated if using react-tweet
  const metrics = {
    likes: 0,
    retweets: 0,
    replies: 0,
  };

  console.log("[Parse oEmbed] Note: Metrics not available via oEmbed. Twitter/X no longer provides engagement counts in public HTML.");

  const tweetData = {
    id: tweetId,
    url: oEmbedData.url,
    author: {
      name: authorName,
      username: username,
      verified: oEmbedData.html.includes('verified'),
      avatar: avatar,
    },
    text: extractTweetText(oEmbedData.html),
    html: extractTweetHTML(oEmbedData.html), // Preserve formatting
    rawHtml: oEmbedData.html, // Keep original for reference
    createdAt: new Date().toISOString(), // oEmbed doesn't provide date
    metrics: metrics,
    media: extractMedia(oEmbedData.html),
  };

  console.log("[Parse oEmbed] Final tweetData object:", {
    id: tweetData.id,
    author: tweetData.author.name,
    metrics: tweetData.metrics,
  });

  return tweetData;
};

/**
 * Check if a string looks like a date
 */
const isDateString = (str) => {
  if (!str) return false;
  // Check for common date patterns
  const datePatterns = [
    /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{1,2},\s+\d{4}$/i,
    /^\d{1,2}\/\d{1,2}\/\d{4}$/,
    /^\d{4}-\d{2}-\d{2}$/,
    /^(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)/i,
  ];
  return datePatterns.some(pattern => pattern.test(str.trim()));
};

/**
 * Extract author name (full display name) from HTML
 */
const extractAuthorName = (html) => {
  if (!html) return "";

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // Method 1: Look for the author name in the blockquote structure
  // Twitter oEmbed typically has the author name in a specific structure
  const blockquote = tempDiv.querySelector("blockquote.twitter-tweet");
  if (blockquote) {
    // Try to find the author name - it's usually in a link or span before the tweet text
    // Look for links that point to the user's profile
    const links = blockquote.querySelectorAll("a");
    for (const link of links) {
      const href = link.getAttribute("href");
      // Author link typically points to twitter.com/username (not status, hashtag, or t.co)
      if (href &&
        (href.includes("twitter.com/") || href.includes("x.com/")) &&
        !href.includes("/status/") &&
        !href.includes("/hashtag/") &&
        !href.includes("t.co") &&
        !href.includes("/i/")) {
        const linkText = link.textContent.trim();
        // Make sure it's not a URL, hashtag, mention, or date
        if (linkText &&
          !linkText.startsWith("http") &&
          !linkText.startsWith("#") &&
          !linkText.startsWith("@") &&
          !linkText.includes("t.co") &&
          !isDateString(linkText) &&
          linkText.length > 1 &&
          linkText.length < 50) {
          return linkText;
        }
      }
    }
  }

  // Method 2: Fallback - try to find any text that looks like a name
  // Look for text that's not a URL, hashtag, mention, or date
  const textNodes = tempDiv.querySelectorAll("a");
  for (const node of textNodes) {
    const text = node.textContent.trim();
    const href = node.getAttribute("href");
    // Skip if it's a URL, hashtag, mention, or date
    if (text &&
      !text.startsWith("http") &&
      !text.startsWith("#") &&
      !text.startsWith("@") &&
      !text.includes("t.co") &&
      !isDateString(text) &&
      text.length > 1 &&
      text.length < 50) { // Reasonable name length
      // Check if href points to a user profile
      if (href && (href.includes("twitter.com/") || href.includes("x.com/"))) {
        const urlMatch = href.match(/(?:twitter\.com|x\.com)\/([^/?#]+)/);
        if (urlMatch && urlMatch[1] &&
          !urlMatch[1].includes("status") &&
          !urlMatch[1].includes("hashtag") &&
          urlMatch[1] !== "i") {
          return text;
        }
      }
    }
  }

  return "";
};

/**
 * Extract username from URL
 */
const extractUsername = (url) => {
  const match = url.match(/twitter\.com\/([^/]+)/);
  return match ? match[1] : "unknown";
};

/**
 * Extract profile picture/avatar from HTML
 */
const extractProfilePicture = (html, username = "") => {
  if (!html) return "";

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // Method 1: Look for profile images in img tags with profile_images in URL
  const images = tempDiv.querySelectorAll("img");
  for (const img of images) {
    const src = img.src || img.getAttribute("src") || img.getAttribute("data-src");
    if (src) {
      // Check for profile image patterns
      if (src.includes("profile_images") ||
        (src.includes("pbs.twimg.com") && !src.includes("media") && !src.includes("ext_tw_video"))) {
        // Upgrade image quality
        let avatarUrl = src
          .replace(/_normal\.(jpg|png|jpeg|webp)/i, "_400x400.$1")
          .replace(/_bigger\.(jpg|png|jpeg|webp)/i, "_400x400.$1")
          .replace(/_mini\.(jpg|png|jpeg|webp)/i, "_400x400.$1")
          .replace(/_reasonably_small\.(jpg|png|jpeg|webp)/i, "_400x400.$1");

        if (avatarUrl && avatarUrl.startsWith("http")) {
          return avatarUrl;
        }
      }
    }
  }

  // Method 2: Look in blockquote structure (Twitter oEmbed format)
  const blockquote = tempDiv.querySelector("blockquote.twitter-tweet");
  if (blockquote) {
    // Check for images inside blockquote
    const blockquoteImages = blockquote.querySelectorAll("img");
    for (const img of blockquoteImages) {
      const src = img.src || img.getAttribute("src") || img.getAttribute("data-src");
      if (src && (src.includes("profile_images") ||
        (src.includes("pbs.twimg.com") && !src.includes("media")))) {
        let avatarUrl = src
          .replace(/_normal\.(jpg|png|jpeg|webp)/i, "_400x400.$1")
          .replace(/_bigger\.(jpg|png|jpeg|webp)/i, "_400x400.$1")
          .replace(/_mini\.(jpg|png|jpeg|webp)/i, "_400x400.$1");
        if (avatarUrl && avatarUrl.startsWith("http")) {
          return avatarUrl;
        }
      }
    }

    // Check for data attributes that might contain profile image URL
    const dataAttrs = blockquote.getAttributeNames().filter(attr => attr.startsWith("data-"));
    for (const attr of dataAttrs) {
      const value = blockquote.getAttribute(attr);
      if (value && value.includes("profile_images")) {
        let avatarUrl = value
          .replace(/_normal\.(jpg|png|jpeg|webp)/i, "_400x400.$1")
          .replace(/_bigger\.(jpg|png|jpeg|webp)/i, "_400x400.$1");
        if (avatarUrl && avatarUrl.startsWith("http")) {
          return avatarUrl;
        }
      }
    }
  }

  // Method 3: Check for background-image in style attributes
  const elementsWithStyle = tempDiv.querySelectorAll("[style]");
  for (const el of elementsWithStyle) {
    const style = el.getAttribute("style");
    if (style) {
      const bgImageMatch = style.match(/url\(['"]?([^'")]*profile_images[^'")]*)['"]?\)/i);
      if (bgImageMatch && bgImageMatch[1]) {
        let avatarUrl = bgImageMatch[1]
          .replace(/_normal\.(jpg|png|jpeg|webp)/i, "_400x400.$1")
          .replace(/_bigger\.(jpg|png|jpeg|webp)/i, "_400x400.$1")
          .replace(/_mini\.(jpg|png|jpeg|webp)/i, "_400x400.$1");
        if (avatarUrl && avatarUrl.startsWith("http")) {
          return avatarUrl;
        }
      }
    }
  }

  // Method 4: Try to extract from HTML string using regex (fallback)
  const profileImageRegex = /(https?:\/\/[^"'\s<>]*profile_images[^"'\s<>]*)/i;
  const match = html.match(profileImageRegex);
  if (match && match[1]) {
    let avatarUrl = match[1]
      .replace(/_normal\.(jpg|png|jpeg|webp)/i, "_400x400.$1")
      .replace(/_bigger\.(jpg|png|jpeg|webp)/i, "_400x400.$1")
      .replace(/_mini\.(jpg|png|jpeg|webp)/i, "_400x400.$1");
    if (avatarUrl && avatarUrl.startsWith("http")) {
      return avatarUrl;
    }
  }

  // Method 5: Try using unavatar.io as a fallback (public service)
  // This will be handled by the async fallback in parseOEmbedData

  return "";
};

/**
 * Extract tweet text from HTML (plain text version)
 */
const extractTweetText = (html) => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  const textElement = tempDiv.querySelector("p");
  return textElement ? textElement.textContent.trim() : "";
};

/**
 * Extract tweet HTML content with formatting preserved
 */
const extractTweetHTML = (html) => {
  if (!html) return "";

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  const textElement = tempDiv.querySelector("p");
  if (!textElement) return "";

  // Get the entire paragraph element with all its attributes and styles
  // This preserves CSS like writing-mode, text-orientation, direction, etc.
  let tweetHTML = textElement.outerHTML;

  // Remove URLs but preserve other formatting and ALL inline styles
  // Remove t.co links and other URLs from anchor tags, but keep the text content
  tweetHTML = tweetHTML.replace(/<a[^>]*href=["']https?:\/\/t\.co\/[^"']+["'][^>]*>([^<]*)<\/a>/gi, "$1");
  tweetHTML = tweetHTML.replace(/<a[^>]*href=["']https?:\/\/[^"']+["'][^>]*>([^<]*)<\/a>/gi, "$1");

  // Clean up empty links
  tweetHTML = tweetHTML.replace(/<a[^>]*><\/a>/gi, "");

  // Preserve all nested elements with their styles
  // Make sure we're keeping span, div, and other elements that might have styling
  const allElements = tempDiv.querySelectorAll("p *");
  allElements.forEach(el => {
    // Ensure all style attributes are preserved
    if (el.hasAttribute('style')) {
      const style = el.getAttribute('style');
      // Keep the style attribute as-is - it contains Twitter's CSS
    }
  });

  // If we removed the <p> tag content entirely, extract innerHTML instead
  const innerContent = textElement.innerHTML.trim();
  if (!tweetHTML.includes('<') && innerContent) {
    // Reconstruct with the <p> tag and its original style attribute
    const pStyle = textElement.getAttribute('style') || '';
    const pClass = textElement.getAttribute('class') || '';
    const pDir = textElement.getAttribute('dir') || '';

    let reconstructed = '<p';
    if (pStyle) reconstructed += ` style="${pStyle.replace(/"/g, '&quot;')}"`;
    if (pClass) reconstructed += ` class="${pClass}"`;
    if (pDir) reconstructed += ` dir="${pDir}"`;
    reconstructed += '>';

    // Remove URLs from innerHTML but preserve everything else
    let cleanedInner = innerContent
      .replace(/<a[^>]*href=["']https?:\/\/t\.co\/[^"']+["'][^>]*>([^<]*)<\/a>/gi, "$1")
      .replace(/<a[^>]*href=["']https?:\/\/[^"']+["'][^>]*>([^<]*)<\/a>/gi, "$1")
      .replace(/<a[^>]*><\/a>/gi, "");

    reconstructed += cleanedInner + '</p>';
    tweetHTML = reconstructed;
  }

  return tweetHTML.trim();
};

/**
 * Extract media from HTML
 */
const extractMedia = (html) => {
  const media = [];
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // Extract images
  const images = tempDiv.querySelectorAll("img");
  images.forEach((img) => {
    if (img.src && !img.src.includes("profile_images") && img.src.trim() !== '') {
      media.push({
        type: "image",
        url: img.src,
        alt: img.alt || "",
      });
    }
  });

  // Filter to only include items with valid URLs
  const validMedia = media.filter(item => item && item.url && item.url.trim() !== '');

  // Return only the first image
  return validMedia.length > 0 ? [validMedia[0]] : [];
};

/**
 * Use react-tweet's public API endpoint to fetch tweet data with metrics
 * 
 * react-tweet provides a CORS-friendly API endpoint: https://react-tweet.vercel.app/api/tweet/:id
 * This endpoint proxies requests to Twitter's syndication API server-side, avoiding CORS issues.
 * 
 * NOTE: For production, you should set up your own API route to avoid rate limiting.
 * See: https://github.com/vercel/react-tweet#enabling-cache-for-the-twitter-api
 */
export const fetchTweetWithReactTweet = async (tweetId) => {
  try {
    // Use react-tweet's public API endpoint (CORS-friendly)
    // This endpoint is provided by react-tweet for client-side usage
    const apiUrl = `https://react-tweet.vercel.app/api/tweet/${tweetId}`;

    console.log("[React-Tweet] Fetching tweet with metrics from react-tweet API...");

    const response = await fetch(apiUrl);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("Tweet not found");
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    // react-tweet API returns { data: Tweet } or { error: string }
    if (result.error) {
      throw new Error(result.error);
    }

    if (!result.data) {
      throw new Error("Tweet not found");
    }

    // Log the raw data structure for debugging
    console.log("[React-Tweet] Raw API response structure:", {
      hasData: !!result.data,
      dataKeys: result.data ? Object.keys(result.data).slice(0, 20) : [],
      sampleData: JSON.stringify(result.data).substring(0, 1000),
    });

    // Also log the full response to see the structure
    if (result.data) {
      console.log("[React-Tweet] Full response data:", result.data);

      // Log the entire response structure to find retweets and views
      console.log("[React-Tweet] Full response object keys:", Object.keys(result));
      console.log("[React-Tweet] Full response object:", result);

      // Check if retweets are in the root response or nested
      if (result.retweet_count !== undefined) {
        console.log("[React-Tweet] Found retweet_count in root:", result.retweet_count);
      }
      if (result.data?.retweet_count !== undefined) {
        console.log("[React-Tweet] Found retweet_count in data:", result.data.retweet_count);
      }

      // Detailed logging for views
      console.log("[React-Tweet] Searching for views in response...");
      console.log("[React-Tweet] result.view_count:", result.view_count);
      console.log("[React-Tweet] result.views:", result.views);
      console.log("[React-Tweet] result.data.view_count:", result.data.view_count);
      console.log("[React-Tweet] result.data.views:", result.data.views);
      console.log("[React-Tweet] result.data.public_metrics:", result.data.public_metrics);

      // Search for any field containing 'view' or 'impression'
      const searchForViewFields = (obj, path = '') => {
        if (typeof obj !== 'object' || obj === null) return;
        for (const key in obj) {
          const keyLower = key.toLowerCase();
          if (keyLower.includes('view') || keyLower.includes('impression')) {
            console.log(`[React-Tweet] Found view-related field at ${path}.${key}:`, obj[key]);
          }
          if (typeof obj[key] === 'object' && obj[key] !== null) {
            searchForViewFields(obj[key], path ? `${path}.${key}` : key);
          }
        }
      };
      searchForViewFields(result);
    }

    console.log("[React-Tweet] Successfully fetched tweet data with metrics");

    // Check if retweets or views are missing - if so, try to fetch from Twitter syndication API directly
    const normalizedData = normalizeReactTweetData(result.data);

    // If retweets are 0, try fetching from Twitter syndication API
    if (normalizedData.metrics.retweets === 0 && normalizedData.metrics.likes > 0) {
      console.log("[React-Tweet] Retweets are missing, attempting to fetch from Twitter syndication API...");
      try {
        const syndicationUrl = `https://cdn.syndication.twimg.com/tweet-result?id=${tweetId}&token=abc123`;
        const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(syndicationUrl)}`;
        const proxyResponse = await fetch(proxyUrl);
        const proxyData = await proxyResponse.json();
        const syndicationData = JSON.parse(proxyData.contents);

        console.log("[React-Tweet] Syndication API response:", syndicationData);

        // Try to extract retweet_count and views from syndication API
        if (syndicationData.retweet_count !== undefined) {
          normalizedData.metrics.retweets = syndicationData.retweet_count;
          console.log("[React-Tweet] Found retweets from syndication API:", syndicationData.retweet_count);
        } else if (syndicationData.public_metrics?.retweet_count !== undefined) {
          normalizedData.metrics.retweets = syndicationData.public_metrics.retweet_count;
          console.log("[React-Tweet] Found retweets from syndication API public_metrics:", syndicationData.public_metrics.retweet_count);
        }

        // If retweets still not found, search recursively
        if (normalizedData.metrics.retweets === 0) {
          // Search recursively in syndication response for retweets
          const searchForRetweets = (obj, path = '') => {
            if (typeof obj !== 'object' || obj === null) return null;
            for (const key in obj) {
              const keyLower = key.toLowerCase();
              if ((keyLower.includes('retweet') || keyLower === 'rt_count') && typeof obj[key] === 'number' && obj[key] > 0) {
                console.log(`[React-Tweet] Found retweets in syndication API at ${path}.${key}:`, obj[key]);
                return obj[key];
              }
              if (typeof obj[key] === 'object') {
                const found = searchForRetweets(obj[key], path ? `${path}.${key}` : key);
                if (found !== null) return found;
              }
            }
            return null;
          };
          const foundRetweets = searchForRetweets(syndicationData);
          if (foundRetweets !== null) {
            normalizedData.metrics.retweets = foundRetweets;
          }
        }
      } catch (syndicationError) {
        console.warn("[React-Tweet] Failed to fetch retweets from syndication API:", syndicationError.message);
      }
    }

    return normalizedData;
  } catch (error) {
    // Log the error for debugging
    console.warn(`[React-Tweet] Failed to fetch tweet data:`, error.message);
    throw error;
  }
};

/**
 * Normalize Twitter syndication API data format to our format
 * The Twitter syndication API returns data in a specific format
 */
const normalizeReactTweetData = (tweetResponse) => {
  if (!tweetResponse) {
    throw new Error("Tweet not found");
  }

  // Twitter syndication API returns data in different possible structures
  // Try to find the tweet data
  let tweetData = tweetResponse;

  // The API might return { data: {...} } or just the tweet object directly
  if (tweetResponse.data) {
    tweetData = tweetResponse.data;
  }

  // Also check for nested structures
  if (tweetResponse.tweet) {
    tweetData = tweetResponse.tweet;
  }

  // Log the actual structure to debug conversation_count
  console.log("[Normalize] Tweet data structure check:", {
    hasData: !!tweetResponse.data,
    hasTweet: !!tweetResponse.tweet,
    conversationCountInResponse: tweetResponse.conversation_count,
    conversationCountInData: tweetResponse.data?.conversation_count,
    conversationCountInTweetData: tweetData.conversation_count,
  });

  // Extract user data
  const user = tweetData.user || tweetData.author || {};

  // Extract metrics - Twitter syndication API provides these
  // The API might return metrics in different structures, so we check multiple places
  console.log("[Normalize] Raw tweet data structure:", {
    hasFavoriteCount: !!tweetData.favorite_count,
    favoriteCount: tweetData.favorite_count,
    hasLikeCount: !!tweetData.like_count,
    likeCount: tweetData.like_count,
    hasRetweetCount: !!tweetData.retweet_count,
    retweetCount: tweetData.retweet_count,
    hasReplyCount: !!tweetData.reply_count,
    replyCount: tweetData.reply_count,
    hasConversationCount: !!tweetData.conversation_count,
    conversationCount: tweetData.conversation_count,
    hasQuoteCount: !!tweetData.quote_count,
    quoteCount: tweetData.quote_count,
    hasViewCount: !!tweetData.view_count,
    viewCount: tweetData.view_count,
    hasViews: !!tweetData.views,
    views: tweetData.views,
    hasPublicMetrics: !!tweetData.public_metrics,
    publicMetrics: tweetData.public_metrics,
    allKeys: Object.keys(tweetData).slice(0, 30), // First 30 keys for debugging
    allNumericValues: Object.keys(tweetData).reduce((acc, key) => {
      const val = tweetData[key];
      if (typeof val === 'number' && val > 0) {
        acc[key] = val;
      }
      return acc;
    }, {}),
  });

  // Initialize metrics with multiple fallback options
  let metrics = {
    likes: 0,
    retweets: 0,
    replies: 0,
  };

  // Extract likes - check favorite_count first (most common in react-tweet API)
  // Use explicit null/undefined checks since 0 is a valid value
  if (tweetData.favorite_count !== undefined && tweetData.favorite_count !== null) {
    metrics.likes = Number(tweetData.favorite_count) || 0;
    console.log(`[Normalize] ✅ Extracted likes from favorite_count: ${metrics.likes}`);
  } else if (tweetData.like_count !== undefined && tweetData.like_count !== null) {
    metrics.likes = Number(tweetData.like_count) || 0;
    console.log(`[Normalize] ✅ Extracted likes from like_count: ${metrics.likes}`);
  } else if (tweetData.public_metrics?.like_count !== undefined && tweetData.public_metrics?.like_count !== null) {
    metrics.likes = Number(tweetData.public_metrics.like_count) || 0;
    console.log(`[Normalize] ✅ Extracted likes from public_metrics.like_count: ${metrics.likes}`);
  } else if (tweetData.public_metrics?.favorite_count !== undefined && tweetData.public_metrics?.favorite_count !== null) {
    metrics.likes = Number(tweetData.public_metrics.favorite_count) || 0;
    console.log(`[Normalize] ✅ Extracted likes from public_metrics.favorite_count: ${metrics.likes}`);
  } else if (tweetData.likes?.count !== undefined && tweetData.likes?.count !== null) {
    metrics.likes = Number(tweetData.likes.count) || 0;
  } else if (tweetData.likes !== undefined && tweetData.likes !== null) {
    metrics.likes = Number(tweetData.likes) || 0;
  }

  // Extract retweets - check multiple possible field names
  // Use explicit null/undefined checks since 0 is a valid value
  if (tweetData.retweet_count !== undefined && tweetData.retweet_count !== null) {
    metrics.retweets = Number(tweetData.retweet_count) || 0;
    console.log(`[Normalize] ✅ Extracted retweets from retweet_count: ${metrics.retweets}`);
  } else if (tweetData.public_metrics?.retweet_count !== undefined && tweetData.public_metrics?.retweet_count !== null) {
    metrics.retweets = Number(tweetData.public_metrics.retweet_count) || 0;
    console.log(`[Normalize] ✅ Extracted retweets from public_metrics.retweet_count: ${metrics.retweets}`);
  } else if (tweetData.retweets?.count !== undefined && tweetData.retweets?.count !== null) {
    metrics.retweets = Number(tweetData.retweets.count) || 0;
  } else if (tweetData.retweets !== undefined && tweetData.retweets !== null) {
    metrics.retweets = Number(tweetData.retweets) || 0;
  } else if (tweetData.share_count !== undefined && tweetData.share_count !== null) {
    metrics.retweets = Number(tweetData.share_count) || 0;
  } else if (tweetData.public_metrics?.share_count !== undefined && tweetData.public_metrics?.share_count !== null) {
    metrics.retweets = Number(tweetData.public_metrics.share_count) || 0;
  } else if (tweetData.quote_count !== undefined && tweetData.quote_count !== null) {
    metrics.retweets = Number(tweetData.quote_count) || 0;
  } else if (tweetData.public_metrics?.quote_count !== undefined && tweetData.public_metrics?.quote_count !== null) {
    metrics.retweets = Number(tweetData.public_metrics.quote_count) || 0;
  }

  // Extract replies - Twitter syndication API uses conversation_count for replies
  // Use explicit null/undefined checks since 0 is a valid value
  if (tweetData.conversation_count !== undefined && tweetData.conversation_count !== null) {
    metrics.replies = Number(tweetData.conversation_count) || 0;
    console.log(`[Normalize] ✅ Extracted replies from conversation_count: ${metrics.replies}`);
  } else if (tweetData.reply_count !== undefined && tweetData.reply_count !== null) {
    metrics.replies = Number(tweetData.reply_count) || 0;
    console.log(`[Normalize] ✅ Extracted replies from reply_count: ${metrics.replies}`);
  } else if (tweetData.public_metrics?.reply_count !== undefined && tweetData.public_metrics?.reply_count !== null) {
    metrics.replies = Number(tweetData.public_metrics.reply_count) || 0;
  } else if (tweetData.replies?.count !== undefined && tweetData.replies?.count !== null) {
    metrics.replies = Number(tweetData.replies.count) || 0;
  } else if (tweetData.replies !== undefined && tweetData.replies !== null) {
    metrics.replies = Number(tweetData.replies) || 0;
  } else if (tweetData.comment_count !== undefined && tweetData.comment_count !== null) {
    metrics.replies = Number(tweetData.comment_count) || 0;
  } else if (tweetData.public_metrics?.comment_count !== undefined && tweetData.public_metrics?.comment_count !== null) {
    metrics.replies = Number(tweetData.public_metrics.comment_count) || 0;
  } else if (tweetData.quote_count !== undefined && tweetData.quote_count !== null) {
    metrics.replies = Number(tweetData.quote_count) || 0;
  } else if (tweetData.public_metrics?.quote_count !== undefined && tweetData.public_metrics?.quote_count !== null) {
    metrics.replies = Number(tweetData.public_metrics.quote_count) || 0;
  }

  console.log("[Normalize] Initial extracted metrics:", metrics);
  console.log("[Normalize] Raw tweetData.favorite_count:", tweetData.favorite_count);
  console.log("[Normalize] Raw tweetData.retweet_count:", tweetData.retweet_count);
  console.log("[Normalize] Raw tweetData.conversation_count:", tweetData.conversation_count);

  // If metrics are still 0, try to find them in nested structures
  if ((metrics.likes === 0 && tweetData.favorite_count > 0) || metrics.retweets === 0 || metrics.replies === 0) {
    console.log("[Normalize] Some metrics are 0, searching deeper in response structure...");

    // First, log ALL numeric fields (not just metric-related) to find retweets
    const findAllNumericFields = (obj, path = '', depth = 0, found = []) => {
      if (depth > 5 || typeof obj !== 'object' || obj === null) return found;

      for (const key in obj) {
        const value = obj[key];
        const currentPath = path ? `${path}.${key}` : key;
        const keyLower = key.toLowerCase();

        // Log ALL numeric values, not just metric-related ones
        if (typeof value === 'number' && value > 0) {
          found.push({ path: currentPath, key, value, keyLower });
        }

        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          findAllNumericFields(value, currentPath, depth + 1, found);
        }
      }
      return found;
    };

    const allNumericFields = findAllNumericFields(tweetData);
    console.log("[Normalize] ALL numeric fields found in response:", allNumericFields);

    // Also log all keys at each level to see structure
    const logAllKeys = (obj, path = '', depth = 0) => {
      if (depth > 3 || typeof obj !== 'object' || obj === null) return;
      const keys = Object.keys(obj);
      console.log(`[Normalize] Keys at ${path || 'root'}:`, keys);
      for (const key of keys.slice(0, 10)) { // Limit to first 10 to avoid spam
        const value = obj[key];
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          logAllKeys(value, path ? `${path}.${key}` : key, depth + 1);
        }
      }
    };
    logAllKeys(tweetData);

    // Sometimes metrics are in a different structure - search recursively
    const searchForMetrics = (obj, path = '', depth = 0) => {
      if (depth > 4 || typeof obj !== 'object' || obj === null) return;

      for (const key in obj) {
        const value = obj[key];
        const currentPath = path ? `${path}.${key}` : key;
        const keyLower = key.toLowerCase();

        // Look for retweet-related fields (check for repost as well, Twitter's new term)
        // Also check for quote_count which sometimes represents retweets
        if ((keyLower.includes('retweet') || keyLower.includes('share') || keyLower === 'rt_count' ||
          keyLower.includes('repost') || (keyLower.includes('quote') && !keyLower.includes('reply'))) &&
          typeof value === 'number' && value > 0) {
          if (metrics.retweets === 0 || value > metrics.retweets) {
            metrics.retweets = value;
            console.log(`[Normalize] Found retweets at ${currentPath}: ${value}`);
          }
        }

        // Look for reply-related fields (conversation_count is the primary field for replies)
        if ((keyLower.includes('reply') || keyLower.includes('comment') || keyLower.includes('quote') || keyLower.includes('conversation')) && typeof value === 'number' && value > 0) {
          // conversation_count is the most reliable for replies from Twitter syndication API
          if (keyLower.includes('conversation')) {
            metrics.replies = value;
            console.log(`[Normalize] Found replies (conversation_count) at ${currentPath}: ${value}`);
          } else if (keyLower.includes('quote') && metrics.replies > 0) {
            // Skip quote_count if we already have replies
          } else if (metrics.replies === 0 || value > metrics.replies) {
            metrics.replies = value;
            console.log(`[Normalize] Found replies at ${currentPath}: ${value}`);
          }
        }

        // Look for view-related fields
        if ((keyLower.includes('view') || keyLower.includes('impression')) && typeof value === 'number' && value > 0) {
          console.log(`[Normalize] Found potential views at ${currentPath}: ${value}`);
        }

        // Recursively search nested objects
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          searchForMetrics(value, currentPath, depth + 1);
        }
      }
    };

    searchForMetrics(tweetData);
    console.log("[Normalize] Final metrics after deep search:", metrics);
  }


  return {
    id: tweetData.id_str || tweetData.id || tweetResponse.id_str || tweetResponse.id,
    url: `https://twitter.com/i/status/${tweetData.id_str || tweetData.id || tweetResponse.id_str || tweetResponse.id}`,
    author: {
      name: user.name || "Unknown",
      username: user.screen_name || user.username || "unknown",
      verified: user.verified || user.is_verified || false,
      avatar: user.profile_image_url_https || user.profile_image_url || "",
    },
    text: tweetData.text || tweetData.full_text || tweetData.body || "",
    html: tweetData.html || null, // Preserve HTML formatting if available
    createdAt: tweetData.created_at || new Date().toISOString(),
    metrics: metrics,
    media: extractMediaFromTweet(tweetData),
    inReplyTo: tweetData.in_reply_to_status_id_str
      ? {
        id: tweetData.in_reply_to_status_id_str,
        username: tweetData.in_reply_to_screen_name,
      }
      : null,
  };
};

/**
 * Extract media from react-tweet data
 */
const extractMediaFromTweet = (tweet) => {
  const media = [];

  // Method 1: Check entities.media (standard Twitter API format)
  if (tweet.entities?.media) {
    tweet.entities.media.forEach((item) => {
      // Only include photos/images, skip videos
      if (item.type === "photo" && (item.media_url_https || item.media_url)) {
        media.push({
          type: "image",
          url: item.media_url_https || item.media_url,
          alt: item.alt_text || "",
        });
      }
    });
  }

  // Method 2: Check photos array (Twitter syndication API format)
  if (tweet.photos && Array.isArray(tweet.photos)) {
    tweet.photos.forEach((photo) => {
      if (photo.url) {
        media.push({
          type: "image",
          url: photo.url,
          alt: photo.alt_text || "",
        });
      }
    });
  }

  // Method 3: Check mediaDetails array (react-tweet API format)
  if (tweet.mediaDetails && Array.isArray(tweet.mediaDetails)) {
    tweet.mediaDetails.forEach((item) => {
      // Only include images, skip videos
      const mediaUrl = item.url || item.media_url_https || item.media_url;
      if (mediaUrl && item.type !== "video" && item.type !== "animated_gif") {
        media.push({
          type: "image",
          url: mediaUrl,
          alt: item.alt_text || item.alt || "",
        });
      }
    });
  }

  // Remove duplicates based on URL
  const uniqueMedia = [];
  const seenUrls = new Set();
  media.forEach((item) => {
    if (!seenUrls.has(item.url)) {
      seenUrls.add(item.url);
      uniqueMedia.push(item);
    }
  });

  console.log("[Normalize] Extracted media:", uniqueMedia.length, "items");

  // Filter to only include items with valid URLs
  const validMedia = uniqueMedia.filter(item => item && item.url && item.url.trim() !== '');

  if (validMedia.length > 0) {
    console.log("[Normalize] First media item:", validMedia[0]);
    // Return only the first image
    return [validMedia[0]];
  }

  console.log("[Normalize] No valid media found");
  return [];
};

